import { useCallback, useEffect, useRef, useState } from 'react';
import './PersoenlicherAssistent.css';
import CuiLitePanel from './panels/CuiLitePanel';

import { z } from 'zod';
import { httpBoundary } from '@werkingflow/trust-boundary';

const ChatSchema = z.object({ sessionId: z.string().min(1), accountId: z.string().optional(), model: z.string().optional(), provider: z.string().optional() });
const ZugangSchema = z.object({ id: z.string(), label: z.string(), provider: z.string(), models: z.array(z.object({ id: z.string(), label: z.string() })), defaultModel: z.string(), quelle: z.string() });
type Chat = z.infer<typeof ChatSchema>;
type Zugang = z.infer<typeof ZugangSchema>;
const antworten = {
  starten: z.object({ chat: ChatSchema }), neustarten: z.object({ chat: ChatSchema }),
  steuerung: z.object({ chat: ChatSchema.nullable(), accounts: z.array(ZugangSchema) }),
  uebergabe: z.object({ sessionId: z.string(), uebergabe: z.string(), quelle: z.string() }),
  stoppen: z.object({ ok: z.literal(true) }),
};
async function api<K extends keyof typeof antworten>(path: K, body?: unknown): Promise<z.infer<(typeof antworten)[K]>> {
  return httpBoundary({ name: 'cui.assistent.' + path, url: '/api/guide/coach/assistent/' + path,
    schema: antworten[path] as z.ZodType<z.infer<(typeof antworten)[K]>>,
    method: body === undefined ? 'GET' : 'POST', body, timeoutMs: body === undefined ? 15000 : 150000,
    onStatus: (status, text) => {
      if (status < 400) return undefined;
      try {
        const e = z.object({ error: z.string() }).safeParse(JSON.parse(text));
        return { throw: new Error(e.success ? e.data.error : `Assistent nicht erreichbar (HTTP ${status}).`) };
      } catch { return { throw: new Error(`Assistent nicht erreichbar (HTTP ${status}).`) }; }
    },
  });
}
/** The chat stays mounted across guide changes; only an explicit restart changes its identity. */
export function PersoenlicherAssistent({ aktiv }: { aktiv: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (offen && !dialog.current?.open) dialog.current?.showModal();
    if (!offen && dialog.current?.open) dialog.current?.close();
  });
  const [chat, setChat] = useState<Chat | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offen, setOffen] = useState(false);
  const [accounts, setAccounts] = useState<Zugang[]>([]);
  const [accountId, setAccountId] = useState('');
  const [model, setModel] = useState('');
  const [uebergabe, setUebergabe] = useState('');
  const [basis, setBasis] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    if (!aktiv || chat) return;
    let abgebrochen = false;
    api('starten', {}).then(d => { if (!abgebrochen) setChat(d.chat); })
      .catch(e => { if (!abgebrochen) setFehler(e.message); });
    return () => { abgebrochen = true; };
  }, [aktiv, chat, versuch]);
  useEffect(() => {
    if (!aktiv || !chat) return;
    let beendet = false;
    const aktualisieren = async () => {
      try {
        const s = await api('steuerung');
        const current = s.chat;
        if (!beendet && current) setChat(c => c?.sessionId === current.sessionId && c?.model === current.model ? c : current);
      } catch { /* User actions surface errors; a background read never replaces the visible chat. */ }
    };
    const timer = window.setInterval(aktualisieren, 15000);
    window.addEventListener('focus', aktualisieren);
    return () => { beendet = true; window.clearInterval(timer); window.removeEventListener('focus', aktualisieren); };
  }, [aktiv, chat?.sessionId]);
  const vorbereiten = useCallback(async () => {
    setBusy(true); setFehler(null);
    // fallback-ok: Fehler bleibt in role=alert sichtbar; Chat und Entwurf werden nicht ersetzt.
    try {
      const [s, u] = await Promise.all([api('steuerung'), api('uebergabe')]);
      const current = s.chat;
      if (!current || current.sessionId !== u.sessionId) throw new Error('Sitzung wurde inzwischen gewechselt. Bitte erneut öffnen.');
      setChat(current); setAccounts(s.accounts); setBasis(u.sessionId); setUebergabe(u.uebergabe);
      const a = s.accounts.find(a => a.id === current.accountId) || s.accounts[0];
      setAccountId(a?.id || ''); setModel(a?.models.some((m: {id:string}) => m.id === current.model) ? current.model || '' : a?.defaultModel || '');
      setOffen(true);
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }, []);
  const neustarten = async () => {
    setBusy(true); setFehler(null);
    // fallback-ok: Fehler bleibt in role=alert sichtbar; kein Erfolgsersatz.
    try {
      const d = await api('neustarten', { sessionId: basis, accountId, model, uebergabe });
      setChat(d.chat); setOffen(false); setHinweis('Neue Sitzung gestartet. Der bisherige Verlauf bleibt erhalten.');
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const stoppen = async () => {
    setBusy(true); setFehler(null);
    // fallback-ok: Fehler bleibt in role=alert sichtbar; kein erfolgreicher Stopp behauptet.
    try { await api('stoppen', { sessionId: chat?.sessionId }); setHinweis('Angehalten. Mit deiner nächsten Nachricht setzt du denselben Chat fort.'); }
    catch (e) { setFehler(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const account = accounts.find(a => a.id === accountId);
  return <div className="pa-shell" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
    {chat && <div className="pa-toolbar">
      <span className="pa-current"><span className="pa-dot" />{chat.provider === 'codex' ? 'Codex' : 'Claude'}<span className="pa-model">{chat.model}</span></span>
      <div className="pa-actions"><button className="pa-button" type="button" disabled={busy} onClick={vorbereiten}>{busy ? 'Bitte warten …' : 'Agent wechseln'}</button>
      <button className="pa-button pa-quiet" type="button" disabled={busy} onClick={stoppen}>Anhalten</button></div>
    </div>}
    {fehler && !offen && <div role="alert" style={{ padding: 10 }}>{fehler} <button type="button" disabled={busy} onClick={async () => {
      setBusy(true);
      // fallback-ok: Fehler bleibt in role=alert sichtbar; bestehender Verlauf bleibt erhalten.
      try { const d = await api('starten', {}); setChat(d.chat); setFehler(null); setVersuch(v => v + 1); }
      catch (e) { setFehler(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
    }}>Erneut öffnen</button></div>}
    {hinweis && <p role="status" style={{ margin: 8 }}>{hinweis}</p>}
    <dialog ref={dialog} className="pa-dialog" aria-labelledby="pa-title" aria-describedby="pa-description"
      onCancel={e => { e.preventDefault(); if (!busy) setOffen(false); }} onClose={() => setOffen(false)}>
      <header className="pa-dialog-header">
        <div><span className="pa-eyebrow">DEIN ASSISTENT</span><h2 id="pa-title">Wie möchtest du weiterarbeiten?</h2></div>
        <button className="pa-close" type="button" disabled={busy} onClick={() => setOffen(false)} aria-label="Dialog schließen">×</button>
      </header>
      <div className="pa-dialog-body">
        <p id="pa-description" className="pa-description">Wähle Agent und Modell für deine neue Sitzung.</p>
        <div className="pa-providers" role="group" aria-label="Agent">
          {['claude', 'codex'].filter(provider => accounts.some(a => a.provider === provider)).map(provider =>
            <button key={provider} type="button" className={'pa-provider' + (account?.provider === provider ? ' pa-selected' : '')}
              aria-pressed={account?.provider === provider} disabled={busy} onClick={() => {
                if (account?.provider === provider) return;
                const a = accounts.find(a => a.provider === provider);
                setAccountId(a?.id || ''); setModel(a?.defaultModel || '');
              }}><span className="pa-provider-name">{provider === 'codex' ? 'Codex' : 'Claude'}</span><span className="pa-provider-caption">{account?.provider === provider ? 'Ausgewählt' : 'Auswählen'}</span></button>)}
        </div>
        <div className="pa-fields">
          <label>Zugang<select value={accountId} disabled={busy} onChange={e => {
            const a = accounts.find(a => a.id === e.target.value); setAccountId(e.target.value); setModel(a?.defaultModel || '');
          }}>{accounts.filter(a => a.provider === account?.provider).map(a => <option key={a.id} value={a.id}>{a.label}</option>)}</select></label>
          <label>Modell<select value={model} disabled={busy} onChange={e => setModel(e.target.value)}>
            {account?.models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select></label>
        </div>
        <div className="pa-handover"><span className="pa-handover-icon" aria-hidden="true">↗</span><div><strong>Mit deinem Arbeitsstand weiter</strong><p>Der Gesprächsauszug wird übergeben. Der bisherige Chat bleibt nachlesbar.</p></div></div>
        <details className="pa-details"><summary>Übergabe ansehen oder ergänzen</summary>
          <label className="pa-editor-label">Gesprächsauszug<textarea value={uebergabe} maxLength={16000} disabled={busy} onChange={e => setUebergabe(e.target.value)} rows={6} /></label>
          <small>Modellauswahl: {account?.quelle}</small>
        </details>
        {fehler && <div className="pa-error" role="alert">{fehler}</div>}
      </div>
      <footer className="pa-dialog-footer"><p>Beim Start wird die bisherige Sitzung angehalten.</p><div className="pa-actions">
        <button className="pa-button" type="button" disabled={busy} onClick={() => setOffen(false)}>Abbrechen</button>
        <button className="pa-button pa-primary" type="button" disabled={busy || !model || !basis} onClick={neustarten}>{busy ? 'Sitzung wird gestartet …' : `Mit ${account?.provider === 'codex' ? 'Codex' : 'Claude'} starten`}</button>
      </div></footer>
    </dialog>
    <div inert={busy} style={{ flex: 1, minHeight: 0 }}>
      {chat ? <CuiLitePanel key={chat.sessionId} panelId="guide-persoenlicher-assistent" initialSessionId={chat.sessionId}
        accountId={chat.accountId} isTabVisible={aktiv} titel="Dein Assistent" /> : !fehler && <p role="status" style={{ padding: 16 }}>Dein Assistent wird geöffnet …</p>}
    </div>
  </div>;
}
